import { useMemo, useState } from "react";
import { useRecoilState } from "recoil";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, HeaderRow, Row, Col } from "../App.styles";
import {
    ConfirmationModal,
    Pagination,
    Refresher,
    Search,
    SidebarLayout,
    Spinner,
    withPlaceholder,
} from "../common";
import HighlightList from "../common/HighlightList";
import { NpcItemHeader } from "./Npc.styles";
import {
    termState,
    useCreateMutation,
    useDestroyMutation,
    useInvalidateInfinity,
    highlightIdState,
    useNpcsQuery,
} from "./Npc.state";
import NpcItem from "./NpcItem";
import NpcPlaceholder from "./NpcPlaceholder";
import { NpcContextProvider } from "./Npc.context";
import { useEmptyGameMutation, useGameQuery } from "../settings/Settings.state";
import AlertDisplay from "../common/AlertDisplay";
import { OrganizeContextProvider } from "../organize/Organize.context";
import { usePageListeners } from "../common/Refresher.state";
import ROUTE_PATTERNS from "../consts/routes";

/**
 * The main NPC (Non-Player Character) dashboard component.
 * Displays a list of NPCs for the game, allowing creators to add, edit, or delete them.
 * Includes search functionality and a super-admin control to empty game chat messages.
 * 
 * Uses `NpcContextProvider` and `OrganizeContextProvider` to pass down necessary state
 * to the individual `NpcItem` components.
 * 
 * @returns {React.ReactElement} The rendered NPC dashboard.
 */
const NpcComponent = () => {
    const { game_id } = useParams();
    const { t } = useTranslation();
    const [openId, setOpenId] = useState();
    const [highlightId, setHighlightId] = useRecoilState(highlightIdState);
    const [term, setTerm] = useRecoilState(termState);
    const [game] = useGameQuery(game_id);
    const data = useNpcsQuery(game_id);
    const npcIds = useMemo(() => data?.map(npc => npc.id) || [], [data]);
    const listeners = usePageListeners(ROUTE_PATTERNS.GAMES_NPC, { npcIds });
    const { isCreating, create } = useCreateMutation();
    const { isDeleting, destroy } = useDestroyMutation();
    const { isEmptyingGame, emptyGame } = useEmptyGameMutation();
    const afterDestroy = useInvalidateInfinity()

    const onCreate = async () => {
        const {
            data,
        } = await create();
        const { id } = data?.non_player_character || {};
        setOpenId(id);
        setHighlightId(id);
    };

    const search = (term) => {
        setTerm(term);
    };

    return (
    <OrganizeContextProvider>
        <NpcContextProvider>
            <AlertDisplay />
            <Row data-testid="npc-item__header">
                <Col size="12" xl="9" className="col--stretch">
                    <HeaderRow className="flex-grow-1 mb-2 g-2">
                        <div className="col-12 col-lg-6 mb-2 mb-lg-0">
                            <Search className="form-control--flat form-control--flat-sm m-0" onChange={search} value={term} />
                        </div>
                        <NpcItemHeader className="col-12 col-lg-6 mb-2 d-block d-sm-flex">
                            <Row className="flex-grow-1 g-2">
                                <Col className="col--stretch col-12 col-lg-auto">
                                    <Button data-testid="npc-item__add-new" size="lg" className="btn--flat btn--flat-sm d-block w-100" color="primary" onClick={onCreate} disabled={isCreating}>
                                        {t('Add New NPC')} {isCreating && <Spinner size="0.875rem" />}
                                    </Button>
                                </Col>
                                <Col className="col--stretch col-12 col-lg-auto">
                                    {
                                        game?.user?.super_admin
                                            ? (
                                                <ConfirmationModal
                                                    title={t("Empty Game")}
                                                    body={t("Are you sure you would like to permanently delete all chat messages in this game?")}
                                                    confirmLabel={t("Yes, empty game")}
                                                    confirmAction={emptyGame}
                                                    isLoading={isEmptyingGame}
                                                >
                                                    <Button data-testid="npc-item__stop" size="lg" className="btn--flat btn--flat-sm d-block w-100" color="danger">
                                                        {t('Empty Game')}
                                                    </Button>
                                                </ConfirmationModal>
                                            )
                                            : null
                                    }
                                </Col>
                            </Row>
                        </NpcItemHeader>
                    </HeaderRow>
                </Col>
            </Row>
            <Row>
                <SidebarLayout
                    message={t("Configure non-player characters.")}
                >
                    <ul className="list-unstyled">
                        { data && data.map((row, index) => (
                            <HighlightList key={row.id} scrollId={highlightId} id={row.id}>
                                <NpcItem
                                    index={index}
                                    {...row}
                                    clearOpenId={() => setOpenId(undefined)}
                                    onDelete={async () => {
                                        await destroy(row.id)
                                        afterDestroy()
                                    }}
                                    isDeleting={isDeleting}
                                    openId={openId}
                                    game_id={game_id}
                                />
                            </HighlightList>
                        ))}
                    </ul>
                    {
                        data?.length === 0
                            ? (
                                <Row>
                                    <Pagination
                                        pages={0 || []}
                                    />
                                </Row>
                            )
                            : null
                    }
                </SidebarLayout>
            </Row>
            <Refresher listeners={listeners} />
        </NpcContextProvider>
    </OrganizeContextProvider>
    );
};

export default withPlaceholder(NpcComponent, NpcPlaceholder);
